﻿import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Slide,
    Stack,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BIBlue } from "../../assets/helpers";
import { English } from "../../constant/EnglishFallback";

/**
 * SaveAsAlertBanner
 *
 * Slides down from the top of its container when `visible` is true.
 * Shows an optional progress bar that fills over `duration` ms.
 * All text and behaviour props are configurable (mirrors the RN component API).
 */
const SaveAsAlertBanner = ({
    visible = false,
    onYes = () => { },
    onNo = () => { },
    loading = false,
    message,
    duration = 10000,
    yesText,
    noText,
    showProgress = true,
    iconSize = 16,
}) => {
    const { multiLanguageData } = useSelector((state) => state?.main);
    const LanguageData = multiLanguageData?.JobPostings || {};
    const {
        TEXT_SAVE_ALERT_MESSAGE = English.JobPostings.TEXT_SAVE_ALERT_MESSAGE,
        TEXT_YES = English.JobPostings.TEXT_YES,
        TEXT_NO = English.JobPostings.TEXT_NO,
    } = LanguageData || {};

    const [progress, setProgress] = useState(0);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    // --- Progress animation (RAF-based, same feel as RN Animated.timing) ---
    const stopProgress = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

  const closeTheModal = () => {
    setTimeout(() => {
      stopProgress();
      onNo();
    }, 11 * 1000);
  }

  useEffect(() => {
      closeTheModal()
        if (visible && showProgress && duration > 0) {
            setProgress(0);
            startTimeRef.current = performance.now();

            const tick = (now) => {
                const elapsed = now - startTimeRef.current;
                const pct = Math.min((elapsed / duration) * 100, 100);
                setProgress(pct);
                if (pct < 100) {
                    rafRef.current = requestAnimationFrame(tick);
                }
            };
            rafRef.current = requestAnimationFrame(tick);
        } else {
            stopProgress();
            setProgress(0);
        }

        return stopProgress;
    }, [visible, duration, showProgress]);

    const handleYes = () => {
        stopProgress();
        onYes();
    };

    const handleNo = () => {
        stopProgress();
        onNo();
    };

    return (
        <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d4f8",
                    borderRadius: "8px",
                    mb: 1,
                    px: 1.25,
                    py: 1,
                    gap: 0.75,
                    overflow: "hidden",
                }}
            >
                {/* Progress bar – sits at the bottom of the banner */}
                {showProgress && (
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            borderRadius: 0,
                            backgroundColor: "#d1ddf8",
                            "& .MuiLinearProgress-bar": { backgroundColor: BIBlue },
                        }}
                    />
                )}

                {/* Icon */}
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "#dce5fb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <NotificationsActiveIcon sx={{ fontSize: iconSize, color: BIBlue }} />
                </Box>

                {/* Message */}
                <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#111" }}>
                    {message || TEXT_SAVE_ALERT_MESSAGE}
                </Typography>

                {/* Actions */}
                <Stack direction="row"  sx={{ gap: 0.625, alignItems: "center" }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleNo}
                        disabled={loading}
                        sx={{
                            minWidth: "unset",
                            px: 1.25,
                            py: 0.625,
                            fontSize: 12,
                            fontWeight: 600,
                            borderColor: "#ccc",
                            color: "#555",
                            "&:hover": { borderColor: "#aaa" },
                        }}
                    >
                        {noText || TEXT_NO}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleYes}
                        disabled={loading}
                        sx={{
                            minWidth: 40,
                            px: 1.5,
                            py: 0.625,
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={12} sx={{ color: "white" }} />
                        ) : (
                            yesText || TEXT_YES
                        )}
                    </Button>
                </Stack>
            </Box>
        </Slide>
    );
};

export default SaveAsAlertBanner;
